import { isConsumerNameFormatValid, isIso3166Alpha2 } from "@payair/e-com-common-sdk/core/Validators"
import {
  ConsumerInfoFieldConfig,
  ConsumerInfoFormConfig,
  ConsumerInfoTextFieldConfig,
} from "../components/consumerInfoForm/ConsumerInfoFormConfig.model"
import { ConsumerInfoValue } from "../components/consumerInfoForm/ConsumerInfoValue.model"
import {
  ConsumerInfoFormError,
  ConsumerInfoFormField,
  ConsumerInfoFormValidity,
} from "../components/consumerInfoForm/ConsumerInfoFormValidity.model"
import {
  CONSUMER_EMAIL_MAX_LENGTH,
  getConsumerPhoneNumberValidationError,
  isConsumerEmailValid,
} from "./ConsumerInputValidation"

export interface ResolvedConsumerInfoFormConfig {
  firstName: ConsumerInfoTextFieldConfig
  lastName: ConsumerInfoTextFieldConfig
  emailAddress: ConsumerInfoFieldConfig
  mobileNumber: ConsumerInfoFieldConfig
  countryCode: ConsumerInfoFieldConfig & { allowedCountries: string[] }
}

const requiredField = (): ConsumerInfoFieldConfig => ({ required: true, disabled: false })
// Network length limits are applied conservatively as JavaScript UTF-16 code units.
const getConsumerNameLength = (value: string): number => value.length

export const resolveConsumerInfoFormConfig = (config?: ConsumerInfoFormConfig): ResolvedConsumerInfoFormConfig => ({
  firstName: { ...requiredField(), maxLength: 30, ...config?.firstName },
  lastName: { ...requiredField(), ...config?.lastName },
  emailAddress: { ...requiredField(), ...config?.emailAddress },
  mobileNumber: { ...requiredField(), ...config?.mobileNumber },
  countryCode: {
    required: false,
    disabled: false,
    ...config?.countryCode,
    allowedCountries: config?.countryCode?.allowedCountries || [],
  },
})

const addError = (
  errors: ConsumerInfoFormError[],
  field: ConsumerInfoFormField,
  code: ConsumerInfoFormError["code"],
  parameters: Pick<ConsumerInfoFormError, "minLength" | "maxLength"> = {}
): void => {
  errors.push({ field, code, ...parameters })
}

const validateText = (
  errors: ConsumerInfoFormError[],
  field: "firstName" | "lastName",
  value: string,
  config: ConsumerInfoTextFieldConfig
): void => {
  const normalizedValue = value.trim()
  if (!normalizedValue) {
    if (config.required) {
      addError(errors, field, "REQUIRED")
    }
    return
  }

  if (
    (config.minLength !== undefined && getConsumerNameLength(normalizedValue) < config.minLength) ||
    (config.maxLength !== undefined && getConsumerNameLength(normalizedValue) > config.maxLength)
  ) {
    addError(errors, field, "INVALID_LENGTH", {
      minLength: config.minLength,
      maxLength: config.maxLength,
    })
  } else if (!isConsumerNameFormatValid(normalizedValue)) {
    addError(errors, field, "INVALID_FORMAT")
  }
}

export const validateConsumerInfo = (
  value: ConsumerInfoValue,
  config?: ConsumerInfoFormConfig,
  externalErrors: ConsumerInfoFormError[] = []
): ConsumerInfoFormValidity => {
  const resolvedConfig = resolveConsumerInfoFormConfig(config)
  const errors: ConsumerInfoFormError[] = []

  validateText(errors, "firstName", value.firstName, resolvedConfig.firstName)
  validateText(errors, "lastName", value.lastName, resolvedConfig.lastName)

  const emailAddress = value.emailAddress.trim()
  if (!emailAddress) {
    if (resolvedConfig.emailAddress.required) {
      addError(errors, "emailAddress", "REQUIRED")
    }
  } else if (emailAddress.length > CONSUMER_EMAIL_MAX_LENGTH) {
    addError(errors, "emailAddress", "INVALID_LENGTH", { maxLength: CONSUMER_EMAIL_MAX_LENGTH })
  } else if (!isConsumerEmailValid(emailAddress)) {
    addError(errors, "emailAddress", "INVALID_FORMAT")
  }

  const hasPhoneCountryCode = Boolean(value.mobileNumber.countryCode)
  const hasSubscriberNumber = Boolean(value.mobileNumber.phoneNumber)
  if (!hasPhoneCountryCode && !hasSubscriberNumber) {
    if (resolvedConfig.mobileNumber.required) {
      addError(errors, "mobileNumber", "REQUIRED")
    }
  } else {
    const phoneError = getConsumerPhoneNumberValidationError(value.mobileNumber)
    if (phoneError) {
      addError(errors, "mobileNumber", phoneError)
    }
  }

  const countryCode = value.countryCode || ""
  if (!countryCode) {
    if (resolvedConfig.countryCode.required) {
      addError(errors, "countryCode", "REQUIRED")
    }
  } else if (
    !isIso3166Alpha2(countryCode) ||
    (resolvedConfig.countryCode.allowedCountries.length > 0 &&
      resolvedConfig.countryCode.allowedCountries.indexOf(countryCode) < 0)
  ) {
    addError(errors, "countryCode", "INVALID_FORMAT")
  }

  const applicableExternalErrors = externalErrors.filter(
    externalError => !errors.some(error => error.field === externalError.field)
  )
  const allErrors = errors.concat(applicableExternalErrors)
  return { valid: allErrors.length === 0, errors: allErrors }
}
